import { Button, Dialog, DialogActions, DialogContent, DialogContentText, Typography } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useMutation } from '@tanstack/react-query';
import { Formik, Form as FormikForm } from 'formik';
import * as React from 'react';
import { toast } from 'react-toastify';
import kitService from 'v2/services/kit';

import type { AddressFormKit, AddressFormValues, AddressKitInfo } from 'v2/interfaces/addressKit';

import Address, { mapCity } from './Address';
import ConfirmUpdateAddress from './ConfirmUpdateAddress';
import { useStyles } from './styles';
import ModalConfirmAddressSchema from './validation';
import DialogTitle from '../DialogTitle';

type ConfirmAddressToKitsProps = {
  isOpen: boolean;
  setIsOpen: (prev: boolean) => void;
  data: AddressKitInfo;
};

const ConfirmAddressToKits = ({ isOpen, setIsOpen, data: { address, name } }: ConfirmAddressToKitsProps) => {
  const theme = useTheme();
  const classes = useStyles();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isOpenConfirmUpdateAddress, setIsOpenConfirmUpdateAddress] = React.useState(false);

  const saveNewAddressToKit = useMutation((data: AddressFormKit) => {
    const formattedData = {
      ...data,
      number: data.number || null,
    };

    return kitService.saveNewAddressToKit(formattedData);
  });

  const onSubmit = async (values: AddressFormValues) => {
    try {
      await saveNewAddressToKit.mutateAsync({
        ...values,
        city: typeof values.city === 'string' ? values.city : values.city.value,
      });
      setIsOpen(false);
      setIsOpenConfirmUpdateAddress(true);
    } catch (error) {
      console.error(error);
      toast.error(error);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  return (
    <>
      <Formik
        initialValues={{
          zipcode: address?.zipcode || '',
          street: address?.street || '',
          neighborhood: address?.neighborhood || '',
          state: address?.state || '',
          number: address?.number || '',
          noNumber: address?.number === null,
          city: address?.city ? mapCity(address?.city, address?.state) : null,
          additionalInfo: address?.additionalInfo || '',
          phone: address?.phone || '',
        }}
        onSubmit={onSubmit}
        validationSchema={ModalConfirmAddressSchema}
      >
        {(formikProps) => {
          return (
            <>
              <FormikForm>
                <Dialog open={isOpen} onClose={setIsOpen} fullScreen={isMobile} maxWidth='md' fullWidth={true}>
                  <DialogTitle onClose={handleClose} disableCloseAction={saveNewAddressToKit.isLoading}>
                    <Typography>📬 Atualizar endereço de entrega!</Typography>
                  </DialogTitle>
                  <DialogContent dividers>
                    <DialogContentText className={classes.dialogContentContainer} color='textPrimary' variant='subtitle1'>
                      <strong>Informe seu novo endereço de entrega:</strong>
                    </DialogContentText>
                    <div className={classes.container}>
                      <Address />
                    </div>
                  </DialogContent>
                  <DialogActions className={classes.buttonActionsContainer}>
                    <Button onClick={handleClose} color='inherit' size='large' disabled={saveNewAddressToKit.isLoading}>
                      Cancelar
                    </Button>
                    <Button
                      variant='contained'
                      color='primary'
                      size='large'
                      type='submit'
                      disabled={saveNewAddressToKit.isLoading}
                      onClick={formikProps.submitForm}
                    >
                      Salvar endereço de entrega!
                    </Button>
                  </DialogActions>
                </Dialog>
              </FormikForm>
            </>
          );
        }}
      </Formik>
      <ConfirmUpdateAddress isOpen={isOpenConfirmUpdateAddress} setIsOpen={setIsOpenConfirmUpdateAddress} name={name} />
    </>
  );
};

export default ConfirmAddressToKits;
